import React from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { serviceAreaBySlug, serviceAreas } from "../data/serviceAreas";
import { servicePages } from "../data/servicePages";
import { SEO } from "./seo";

const SITE_URL = "https://clintonncpressurewashing.com";
const TOP_SERVICE_SLUGS = [
  "house-washing",
  "driveway-cleaning",
  "roof-washing",
  "commercial-pressure-washing",
];

const buildAreaSchemas = (serviceArea) => {
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "HomeAndConstructionBusiness",
    name: "Hamilton Pressure Washing",
    url: `${SITE_URL}/service-areas/${serviceArea.slug}`,
    telephone: "+1-910-990-1974",
    areaServed: {
      "@type": "City",
      name: `${serviceArea.city}, ${serviceArea.state}`,
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: SITE_URL,
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Service Areas",
        item: `${SITE_URL}/service-areas`,
      },
      {
        "@type": "ListItem",
        position: 3,
        name: `${serviceArea.city}, ${serviceArea.state}`,
        item: `${SITE_URL}/service-areas/${serviceArea.slug}`,
      },
    ],
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: serviceArea.faq.map((entry) => ({
      "@type": "Question",
      name: entry.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: entry.answer,
      },
    })),
  };

  return [localBusinessSchema, breadcrumbSchema, faqSchema];
};

export const ServiceAreaDetailPage = () => {
  const { slug } = useParams();
  const serviceArea = slug ? serviceAreaBySlug[slug] : null;

  if (!serviceArea) {
    return <Navigate to="/service-areas" replace />;
  }

  const recommendedServices = servicePages.filter((service) =>
    TOP_SERVICE_SLUGS.includes(service.slug)
  );
  const nearbyAreas = serviceAreas
    .filter((entry) => entry.slug !== serviceArea.slug)
    .slice(0, 4);

  return (
    <>
      <SEO
        title={serviceArea.metaTitle}
        description={serviceArea.metaDescription}
        schemaData={buildAreaSchemas(serviceArea)}
      />

      <main id="service-area-detail-page">
        <section className="service-detail-hero service-area-hero">
          <div className="container">
            <p className="service-detail-eyebrow">Now Serving {serviceArea.county}</p>
            <h1>{serviceArea.heroTitle}</h1>
            <p className="service-detail-intro">{serviceArea.intro}</p>
            <div className="service-detail-actions">
              <Link to="/contact" className="btn btn-custom btn-lg">
                Get free quote
              </Link>
              <a href="tel:+19109901974" className="btn btn-custom btn-lg service-detail-call">
                Call (910) 990-1974
              </a>
            </div>
          </div>
        </section>

        <section className="service-detail-content">
          <div className="container">
            <div className="row">
              <div className="col-md-8">
                {serviceArea.sections.map((section) => (
                  <article key={section.heading} className="service-detail-section">
                    <h2>{section.heading}</h2>
                    {section.paragraphs.map((paragraph) => (
                      <p key={paragraph}>{paragraph}</p>
                    ))}
                  </article>
                ))}

                <div className="service-detail-faq">
                  <h2>Frequently Asked Questions</h2>
                  {serviceArea.faq.map((entry) => (
                    <article key={entry.question} className="service-detail-faq-item">
                      <h3>{entry.question}</h3>
                      <p>{entry.answer}</p>
                    </article>
                  ))}
                </div>
              </div>

              <div className="col-md-4">
                <aside className="service-detail-sidebar">
                  <h3>Top Services In {serviceArea.city}</h3>
                  <ul>
                    {recommendedServices.map((service) => (
                      <li key={service.slug}>
                        <Link to={`/services/${service.slug}`}>
                          {service.name} in {serviceArea.city}, {serviceArea.state}
                        </Link>
                      </li>
                    ))}
                  </ul>

                  <div className="service-detail-links">
                    <h4>Helpful Links</h4>
                    <ul>
                      <li>
                        <Link to="/services">pressure washing services</Link>
                      </li>
                      <li>
                        <Link to="/gallery">recent exterior cleaning work</Link>
                      </li>
                      <li>
                        <Link to="/contact">request a free quote</Link>
                      </li>
                    </ul>
                  </div>

                  <div className="service-detail-sidebar-cta">
                    <p>
                      Need pressure washing in {serviceArea.city}, {serviceArea.state}? Request
                      your fast, free estimate today.
                    </p>
                    <Link to="/contact" className="btn btn-custom">
                      Request Quote
                    </Link>
                  </div>
                </aside>
              </div>
            </div>

            <div className="service-detail-related">
              <h3>Nearby Service Areas</h3>
              <div className="row">
                {nearbyAreas.map((area) => (
                  <div key={area.slug} className="col-sm-6 col-md-3">
                    <Link
                      to={`/service-areas/${area.slug}`}
                      className="service-detail-related-card"
                    >
                      <h4>
                        {area.city}, {area.state}
                      </h4>
                      <p>{area.metaDescription}</p>
                      <span>Explore Area</span>
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};
